import { FC, MouseEvent, useCallback, useMemo } from "react";
import { Button, Tooltip } from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import { DimensionIcon } from "@/components/pages/dimensions/components/icons/DimensionIcon";
import { FilterTreeNodeWithId } from "@/components/pages/common/filters-tree/utils/types/common";
import { filterTreeFiltersAreInvalid } from "@/components/pages/common/filters-tree/utils/helpers/filterTreeFiltersAreInvalid";
import { isTreeNodeFilterGroup } from "@/components/pages/common/filters-tree/utils/helpers/isTreeNodeWithIdFilterGroup";

interface BillingExplorerFilterTreeAddToDimensionButtonProps {
  filterTree?: FilterTreeNodeWithId;
  open: boolean;
  openMenu(event: MouseEvent<HTMLButtonElement | HTMLLIElement>): void;
}

export const BillingExplorerFilterTreeAddToDimensionButton: FC<
  BillingExplorerFilterTreeAddToDimensionButtonProps
> = ({ filterTree, open, openMenu }) => {
  const clickHandler = useCallback(
    (event: MouseEvent<HTMLButtonElement | HTMLLIElement>) => {
      openMenu(event);
    },
    [openMenu],
  );

  const invalid = useMemo(() => {
    return filterTree && filterTreeFiltersAreInvalid(filterTree);
  }, [filterTree]);

  const disabled =
    invalid ||
    !filterTree ||
    (isTreeNodeFilterGroup(filterTree) && !filterTree?.items?.length);

  const tooltipTitle = useMemo(() => {
    if (invalid) {
      return "Select filter values to enable Add to Dimension.";
    }
    if (
      !filterTree ||
      (isTreeNodeFilterGroup(filterTree) && !filterTree?.items?.length)
    ) {
      return "Add at least one filter to Add To Dimension.";
    }
  }, [invalid, filterTree]);

  return (
    <Tooltip arrow placement="top" title={tooltipTitle}>
      <span>
        <Button
          onClick={clickHandler}
          variant="text"
          size="small"
          startIcon={
            <DimensionIcon
              color={disabled ? "disabled" : "primary"}
              fontSize="small"
            />
          }
          endIcon={
            !open ? (
              <ArrowDropDownIcon fontSize="small" />
            ) : (
              <ArrowDropUpIcon fontSize="small" />
            )
          }
          disabled={disabled}
        >
          Add to Dimension
        </Button>
      </span>
    </Tooltip>
  );
};
